import { useCallback, useMemo } from "react";

import {
  type QueryClient,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { produce } from "immer";

import { caseHintsApi } from "src/api/endpoints";
import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { JsonApiResourceDeserialized } from "src/api/jsonApiTypes";
import { useCurrentWorkspace } from "src/api/queries";
import { casesKeys, incrementEtag } from "src/cases/queries/cases";
import { TaskIdExtractor } from "src/cases/queries/casesTypes";
import {
  ActionTriggeredEventDataActionFormDataValue,
  CaseActionFieldHintApproveResponse,
  CaseActionHintApproveResponse,
  CaseActionHintPatchResponse,
  CaseHintsResponse,
  CaseResponse,
  CaseTaskFieldHintApproveResponse,
  CaseTaskHintApproveResponse,
  ModelsCaseDataApiModelsCaseActionFieldHintApproveRequestDataTypeEnum,
  ModelsCaseDataApiModelsCaseActionHintApproveRequestDataTypeEnum,
  ModelsCaseDataApiModelsCaseActionHintPatchRequestDataTypeEnum,
  ModelsCaseDataApiModelsCaseTaskFieldHintApproveRequestDataTypeEnum,
  ModelsCaseDataApiModelsCaseTaskHintApproveRequestDataTypeEnum,
  Value,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type CaseHintsDeserialized = JsonApiResourceDeserialized<CaseHintsResponse, {}>;

export const caseHintsKeys = {
  all: (workspaceId: string) => ["caseHints", workspaceId] as const,
  get: (workspaceId: string, caseId: string) =>
    [...caseHintsKeys.all(workspaceId), "hints", caseId] as const,
};

const useCaseHintsClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () => (workspace?.base_url ? caseHintsApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );
};

const useHintsWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCaseHintsClient();

  return { wsId, apiClient };
};

const HINTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

export type CaseHintsData = CaseHintsDeserialized["data"];

export const useCaseHints = <TData = CaseHintsData>(
  caseId?: string,
  options?: Omit<
    UseQueryOptions<CaseHintsData, AxiosError, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { wsId, apiClient } = useHintsWorkspaceParams();

  return useQuery<CaseHintsData, AxiosError, TData>({
    queryKey: caseHintsKeys.get(wsId, caseId!),
    queryFn: async () => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is missing");
      }

      const response = await apiClient.getCaseHintsApiV1CasesCaseIdHintsGet({
        caseId,
      });
      return deserializeJsonApi<CaseHintsDeserialized>(response.data).data;
    },
    staleTime: HINTS_STALE_TIME,
    ...options,
    enabled: !!apiClient && !!caseId && (options?.enabled ?? true),
  });
};

const hasNonEmptyFieldHints = (
  formHints:
    | Record<string, Record<string, Record<string, unknown>>>
    | undefined,
): boolean =>
  !!formHints &&
  Object.values(formHints).some((statusHints) =>
    Object.values(statusHints).some(
      (fieldHints) => Object.keys(fieldHints).length > 0,
    ),
  );

export const hasNonEmptyHints = (data: CaseHintsData): boolean =>
  hasNonEmptyFieldHints(data.task_form_hints) ||
  hasNonEmptyFieldHints(data.action_form_hints) ||
  !!data.hinted_next_action_key;

export const selectTaskKeysWithHints = (
  data: CaseHintsData,
  statusKey?: string,
): Set<string> => {
  const hints = data.task_form_hints;
  if (!hints) return new Set<string>();

  if (statusKey) {
    const statusHints = hints[statusKey];
    if (!statusHints) return new Set<string>();
    return new Set(
      Object.entries(statusHints)
        .filter(([, fieldHints]) => Object.keys(fieldHints).length > 0)
        .map(([taskKey]) => taskKey),
    );
  }

  return new Set(
    Object.values(hints).flatMap((statusHints) =>
      Object.entries(statusHints)
        .filter(([, fieldHints]) => Object.keys(fieldHints).length > 0)
        .map(([taskKey]) => taskKey),
    ),
  );
};

export const useTasksWithHints = (
  caseId?: string,
  options?: Pick<
    UseQueryOptions<CaseHintsData, AxiosError>,
    "enabled" | "refetchInterval"
  >,
  statusKey?: string,
) => {
  const select = useCallback(
    (data: CaseHintsData) => selectTaskKeysWithHints(data, statusKey),
    [statusKey],
  );

  const { data } = useCaseHints(caseId, {
    select,
    ...options,
  });

  return data ?? new Set<string>();
};

export const useHintedNextActionKey = (
  caseId?: string,
  options?: { enabled?: boolean },
) => {
  const { data } = useCaseHints(caseId, {
    select: (data) => data.hinted_next_action_key,
    ...options,
  });
  return data;
};

export const selectTaskFieldHints = (
  data: CaseHintsData,
  taskId: string,
): Record<string, unknown> | undefined => {
  const statusKey = TaskIdExtractor.getStatusKey(taskId);
  const taskKey = TaskIdExtractor.getTaskKey(taskId);
  if (!statusKey || !taskKey) return undefined;

  const statusHints = data.task_form_hints?.[statusKey];
  if (!statusHints) return undefined;

  const fieldHints = statusHints[taskKey];
  if (!fieldHints || Object.keys(fieldHints).length === 0) return undefined;

  return fieldHints as Record<string, unknown>;
};

export const useTaskFieldHints = (
  caseId?: string,
  taskId?: string,
  options?: { enabled?: boolean },
) => {
  return useCaseHints(caseId, {
    select: (data) => (taskId ? selectTaskFieldHints(data, taskId) : undefined),
    ...options,
  });
};

type PatchActionHintParams = {
  caseId: string;
  actionKey: string;
  hintId: string;
  attributes: Record<string, ActionTriggeredEventDataActionFormDataValue>;
};

export const usePatchActionHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionHintPatchResponse,
    AxiosError,
    PatchActionHintParams
  >({
    mutationFn: async ({ caseId, actionKey, hintId, attributes }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.patchCaseActionHintApiV1CasesCaseIdActionsActionKeyHintPatch(
          {
            caseId,
            actionKey,
            caseActionHintPatchRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseActionHintPatchRequestDataTypeEnum.CASE_ACTION_HINT,
                id: hintId,
                attributes,
              },
            },
          },
        );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type ApproveTaskHintParams = {
  caseId: string;
  taskKey: string;
  hintId: string;
  ifMatch: string;
  attributes: Record<string, ActionTriggeredEventDataActionFormDataValue>;
};

export const useApproveTaskHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskHintApproveResponse,
    AxiosError,
    ApproveTaskHintParams
  >({
    mutationFn: async ({ caseId, taskKey, hintId, ifMatch, attributes }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.approveCaseTaskHintApiV1CasesCaseIdTasksTaskKeyHintApprovePost(
          {
            caseId,
            taskKey,
            ifMatch,
            caseTaskHintApproveRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseTaskHintApproveRequestDataTypeEnum.CASE_TASK_HINT_APPROVE,
                id: hintId,
                attributes,
              },
            },
          },
        );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.caseId),
      });
    },
  });
};

const optimisticallyRemoveTaskHints = (
  queryClient: QueryClient,
  wsId: string,
  caseId: string,
  taskKey: string,
): CaseHintsData | undefined => {
  const [statusKey, plainTaskKey] =
    TaskIdExtractor.splitStatusAndTaskKey(taskKey);
  const previousHints = queryClient.getQueryData<CaseHintsData>(
    caseHintsKeys.get(wsId, caseId),
  );
  if (previousHints) {
    queryClient.setQueryData<CaseHintsData>(
      caseHintsKeys.get(wsId, caseId),
      (old) =>
        produce(old, (draft) => {
          if (draft?.task_form_hints?.[statusKey]?.[plainTaskKey]) {
            delete draft.task_form_hints[statusKey][plainTaskKey];
          }
        }),
    );
  }
  return previousHints;
};

const optimisticallyRemoveActionHints = (
  queryClient: QueryClient,
  wsId: string,
  caseId: string,
  actionKey: string,
): CaseHintsData | undefined => {
  const [statusKey, plainActionKey] =
    TaskIdExtractor.splitStatusAndTaskKey(actionKey);
  const previousHints = queryClient.getQueryData<CaseHintsData>(
    caseHintsKeys.get(wsId, caseId),
  );
  if (previousHints) {
    queryClient.setQueryData<CaseHintsData>(
      caseHintsKeys.get(wsId, caseId),
      (old) =>
        produce(old, (draft) => {
          if (draft?.action_form_hints?.[statusKey]?.[plainActionKey]) {
            delete draft.action_form_hints[statusKey][plainActionKey];
          }
        }),
    );
  }
  return previousHints;
};

// Leave meta.etag untouched: rejecting a hint doesn't advance it server-side,
// and the save that follows needs it for If-Match.
const optimisticallyClearCaseFormValues = (
  queryClient: QueryClient,
  wsId: string,
  caseId: string,
  formResource: {
    type: "case_task_forms" | "case_action_forms";
    id: string;
  },
  emptyValues: Record<string, unknown>,
): CaseResponse | undefined => {
  const previousCase = queryClient.getQueryData<CaseResponse>(
    casesKeys.get(wsId, caseId),
  );
  if (previousCase) {
    queryClient.setQueryData<CaseResponse>(casesKeys.get(wsId, caseId), (old) =>
      produce(old, (draft) => {
        if (!draft?.included) return;
        const form = draft.included.find(
          (item) =>
            item.type === formResource.type && item.id === formResource.id,
        );
        if (!form) return;
        Object.assign(form.attributes as Record<string, unknown>, emptyValues);
      }),
    );
  }
  return previousCase;
};

type RejectHintContext = {
  previousHints: CaseHintsData | undefined;
  previousCase: CaseResponse | undefined;
};

type RejectTaskHintParams = {
  caseId: string;
  taskKey: string;
  /** Empty values of the form being cleared. When set, empties the cached form values atomically with the hint removal. */
  clearFormValues?: Record<string, unknown>;
};

export const useRejectTaskHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, RejectTaskHintParams, RejectHintContext>(
    {
      mutationFn: async ({ caseId, taskKey }) => {
        if (!apiClient) {
          throw new Error("API client is missing");
        }

        await apiClient.rejectCaseTaskHintApiV1CasesCaseIdTasksTaskKeyHintRejectPost(
          {
            caseId,
            taskKey,
          },
        );
      },
      onMutate: async ({ caseId, taskKey, clearFormValues }) => {
        // Cancel in-flight refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({
          queryKey: caseHintsKeys.get(wsId, caseId),
        });
        await queryClient.cancelQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });

        const previousHints = optimisticallyRemoveTaskHints(
          queryClient,
          wsId,
          caseId,
          taskKey,
        );

        const previousCase = clearFormValues
          ? optimisticallyClearCaseFormValues(
              queryClient,
              wsId,
              caseId,
              { type: "case_task_forms", id: `${caseId}#${taskKey}` },
              clearFormValues,
            )
          : undefined;

        return { previousHints, previousCase };
      },
      onError: (_err, { caseId }, context) => {
        if (context?.previousHints) {
          queryClient.setQueryData(
            caseHintsKeys.get(wsId, caseId),
            context.previousHints,
          );
        }
        if (context?.previousCase) {
          queryClient.setQueryData(
            casesKeys.get(wsId, caseId),
            context.previousCase,
          );
        }
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: caseHintsKeys.get(wsId, variables.caseId),
        });
      },
    },
  );
};

type RejectActionHintParams = {
  caseId: string;
  actionKey: string;
  /** Empty values of the form being cleared. When set, empties the cached form values atomically with the hint removal. */
  clearFormValues?: Record<string, unknown>;
};

export const useRejectActionHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    RejectActionHintParams,
    RejectHintContext
  >({
    mutationFn: async ({ caseId, actionKey }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      await apiClient.rejectCaseActionHintApiV1CasesCaseIdActionsActionKeyHintRejectPost(
        {
          caseId,
          actionKey,
        },
      );
    },
    onMutate: async ({ caseId, actionKey, clearFormValues }) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });
      await queryClient.cancelQueries({
        queryKey: casesKeys.get(wsId, caseId),
      });

      const previousHints = optimisticallyRemoveActionHints(
        queryClient,
        wsId,
        caseId,
        actionKey,
      );

      const previousCase = clearFormValues
        ? optimisticallyClearCaseFormValues(
            queryClient,
            wsId,
            caseId,
            { type: "case_action_forms", id: `${caseId}#${actionKey}#form` },
            clearFormValues,
          )
        : undefined;

      return { previousHints, previousCase };
    },
    onError: (_err, { caseId }, context) => {
      if (context?.previousHints) {
        queryClient.setQueryData(
          caseHintsKeys.get(wsId, caseId),
          context.previousHints,
        );
      }
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, caseId),
          context.previousCase,
        );
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type ApproveActionHintParams = {
  caseId: string;
  actionKey: string;
  hintId: string;
  ifMatch: string;
  attributes: Record<string, ActionTriggeredEventDataActionFormDataValue>;
};

export const useApproveActionHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionHintApproveResponse,
    AxiosError,
    ApproveActionHintParams
  >({
    mutationFn: async ({ caseId, actionKey, hintId, ifMatch, attributes }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.approveCaseActionHintApiV1CasesCaseIdActionsActionKeyHintApprovePost(
          {
            caseId,
            actionKey,
            ifMatch,
            caseActionHintApproveRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseActionHintApproveRequestDataTypeEnum.CASE_ACTION_HINT_APPROVE,
                id: hintId,
                attributes,
              },
            },
          },
        );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type ApproveTaskFieldHintParams = {
  caseId: string;
  taskKey: string;
  fieldKey: string;
  hintId: string;
  ifMatch: string;
  value: Value | null;
};

const optimisticallyRemoveTaskFieldHint = (
  queryClient: QueryClient,
  wsId: string,
  caseId: string,
  taskKey: string,
  fieldKey: string,
): CaseHintsData | undefined => {
  const [statusKey, plainTaskKey] =
    TaskIdExtractor.splitStatusAndTaskKey(taskKey);
  const previousHints = queryClient.getQueryData<CaseHintsData>(
    caseHintsKeys.get(wsId, caseId),
  );
  if (previousHints) {
    queryClient.setQueryData<CaseHintsData>(
      caseHintsKeys.get(wsId, caseId),
      (old) =>
        produce(old, (draft) => {
          if (draft?.task_form_hints?.[statusKey]?.[plainTaskKey]) {
            delete draft.task_form_hints[statusKey][plainTaskKey][fieldKey];
          }
        }),
    );
  }
  return previousHints;
};

export const useApproveTaskFieldHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskFieldHintApproveResponse,
    AxiosError,
    ApproveTaskFieldHintParams,
    {
      previousHints: CaseHintsData | undefined;
      previousCase: CaseResponse | undefined;
    }
  >({
    mutationFn: async ({
      caseId,
      taskKey,
      fieldKey,
      hintId,
      ifMatch,
      value,
    }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.approveCaseTaskFieldHintApiV1CasesCaseIdTasksTaskKeyHintFieldKeyApprovePost(
          {
            caseId,
            taskKey,
            fieldKey,
            ifMatch,
            caseTaskFieldHintApproveRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseTaskFieldHintApproveRequestDataTypeEnum.CASE_TASK_FIELD_HINT_APPROVE,
                id: hintId,
                attributes: { value },
              },
            },
          },
        );
      return response.data;
    },
    onMutate: async ({ caseId, taskKey, fieldKey, value }) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });
      await queryClient.cancelQueries({
        queryKey: casesKeys.get(wsId, caseId),
      });

      const previousHints = optimisticallyRemoveTaskFieldHint(
        queryClient,
        wsId,
        caseId,
        taskKey,
        fieldKey,
      );

      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );

      if (previousCase) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, caseId),
          (old) =>
            produce(old, (draft) => {
              if (!draft?.included) return;
              const taskResourceId = `${caseId}#${taskKey}`;
              const caseTaskForm = draft.included.find(
                (item) =>
                  item.type === "case_task_forms" && item.id === taskResourceId,
              );
              if (!caseTaskForm) return;
              (caseTaskForm.attributes as Record<string, unknown>)[fieldKey] =
                value;
              const meta = caseTaskForm.meta as { etag: string } | null;
              if (meta?.etag !== undefined) {
                (caseTaskForm.meta as { etag: string }).etag = incrementEtag(
                  meta.etag,
                );
              }
            }),
        );
      }

      return { previousHints, previousCase };
    },
    onError: (_err, { caseId }, context) => {
      if (context?.previousHints) {
        queryClient.setQueryData(
          caseHintsKeys.get(wsId, caseId),
          context.previousHints,
        );
      }
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, caseId),
          context.previousCase,
        );
      }
      toastActions.failure({ title: "Failed to approve suggestion" });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type RejectTaskFieldHintParams = {
  caseId: string;
  taskKey: string;
  fieldKey: string;
};

export const useRejectTaskFieldHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    RejectTaskFieldHintParams,
    { previousHints: CaseHintsData | undefined }
  >({
    mutationFn: async ({ caseId, taskKey, fieldKey }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      await apiClient.rejectCaseTaskFieldHintApiV1CasesCaseIdTasksTaskKeyHintFieldKeyRejectPost(
        {
          caseId,
          taskKey,
          fieldKey,
        },
      );
    },
    onMutate: async ({ caseId, taskKey, fieldKey }) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });

      const previousHints = optimisticallyRemoveTaskFieldHint(
        queryClient,
        wsId,
        caseId,
        taskKey,
        fieldKey,
      );

      return { previousHints };
    },
    onError: (_err, { caseId }, context) => {
      if (context?.previousHints) {
        queryClient.setQueryData(
          caseHintsKeys.get(wsId, caseId),
          context.previousHints,
        );
      }
      toastActions.failure({ title: "Failed to reject suggestion" });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};

export const selectActionFieldHints = (
  data: CaseHintsData,
  actionId: string,
): Record<string, unknown> | undefined => {
  const statusKey = TaskIdExtractor.getStatusKey(actionId);
  const actionKey = TaskIdExtractor.getTaskKey(actionId);
  if (!statusKey || !actionKey) return undefined;

  const statusHints = data.action_form_hints?.[statusKey];
  if (!statusHints) return undefined;

  const fieldHints = statusHints[actionKey];
  if (!fieldHints || Object.keys(fieldHints).length === 0) return undefined;

  return fieldHints as Record<string, unknown>;
};

export const useActionFieldHints = (
  caseId?: string,
  actionId?: string,
  options?: { enabled?: boolean },
) => {
  return useCaseHints(caseId, {
    select: (data) =>
      actionId ? selectActionFieldHints(data, actionId) : undefined,
    ...options,
  });
};

type ApproveActionFieldHintParams = {
  caseId: string;
  actionKey: string;
  fieldKey: string;
  hintId: string;
  ifMatch: string;
  value: Value | null;
};

const optimisticallyRemoveActionFieldHint = (
  queryClient: QueryClient,
  wsId: string,
  caseId: string,
  actionKey: string,
  fieldKey: string,
): CaseHintsData | undefined => {
  const [statusKey, plainActionKey] =
    TaskIdExtractor.splitStatusAndTaskKey(actionKey);
  const previousHints = queryClient.getQueryData<CaseHintsData>(
    caseHintsKeys.get(wsId, caseId),
  );
  if (previousHints) {
    queryClient.setQueryData<CaseHintsData>(
      caseHintsKeys.get(wsId, caseId),
      (old) =>
        produce(old, (draft) => {
          if (draft?.action_form_hints?.[statusKey]?.[plainActionKey]) {
            delete draft.action_form_hints[statusKey][plainActionKey][fieldKey];
          }
        }),
    );
  }
  return previousHints;
};

export const useApproveActionFieldHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionFieldHintApproveResponse,
    AxiosError,
    ApproveActionFieldHintParams,
    {
      previousHints: CaseHintsData | undefined;
      previousCase: CaseResponse | undefined;
    }
  >({
    mutationFn: async ({
      caseId,
      actionKey,
      fieldKey,
      hintId,
      ifMatch,
      value,
    }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.approveCaseActionFieldHintApiV1CasesCaseIdActionsActionKeyHintFieldKeyApprovePost(
          {
            caseId,
            actionKey,
            fieldKey,
            ifMatch,
            caseActionFieldHintApproveRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseActionFieldHintApproveRequestDataTypeEnum.CASE_ACTION_FIELD_HINT_APPROVE,
                id: hintId,
                attributes: { value },
              },
            },
          },
        );
      return response.data;
    },
    onMutate: async ({ caseId, actionKey, fieldKey, value }) => {
      await queryClient.cancelQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });
      await queryClient.cancelQueries({
        queryKey: casesKeys.get(wsId, caseId),
      });

      const previousHints = optimisticallyRemoveActionFieldHint(
        queryClient,
        wsId,
        caseId,
        actionKey,
        fieldKey,
      );

      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );

      if (previousCase) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, caseId),
          (old) =>
            produce(old, (draft) => {
              if (!draft?.included) return;
              const actionResourceId = `${caseId}#${actionKey}#form`;
              const caseActionForm = draft.included.find(
                (item) =>
                  item.type === "case_action_forms" &&
                  item.id === actionResourceId,
              );
              if (!caseActionForm) return;
              (caseActionForm.attributes as Record<string, unknown>)[fieldKey] =
                value;
              const meta = caseActionForm.meta as { etag: string } | null;
              if (meta?.etag !== undefined) {
                (caseActionForm.meta as { etag: string }).etag = incrementEtag(
                  meta.etag,
                );
              }
            }),
        );
      }

      return { previousHints, previousCase };
    },
    onError: (_err, { caseId }, context) => {
      if (context?.previousHints) {
        queryClient.setQueryData(
          caseHintsKeys.get(wsId, caseId),
          context.previousHints,
        );
      }
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, caseId),
          context.previousCase,
        );
      }
      toastActions.failure({ title: "Failed to approve suggestion" });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type RejectActionFieldHintParams = {
  caseId: string;
  actionKey: string;
  fieldKey: string;
};

export const useRejectActionFieldHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    RejectActionFieldHintParams,
    { previousHints: CaseHintsData | undefined }
  >({
    mutationFn: async ({ caseId, actionKey, fieldKey }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      await apiClient.rejectCaseActionFieldHintApiV1CasesCaseIdActionsActionKeyHintFieldKeyRejectPost(
        {
          caseId,
          actionKey,
          fieldKey,
        },
      );
    },
    onMutate: async ({ caseId, actionKey, fieldKey }) => {
      await queryClient.cancelQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });

      const previousHints = optimisticallyRemoveActionFieldHint(
        queryClient,
        wsId,
        caseId,
        actionKey,
        fieldKey,
      );

      return { previousHints };
    },
    onError: (_err, { caseId }, context) => {
      if (context?.previousHints) {
        queryClient.setQueryData(
          caseHintsKeys.get(wsId, caseId),
          context.previousHints,
        );
      }
      toastActions.failure({ title: "Failed to reject suggestion" });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};
